import React, { Component } from "react";
import * as faceapi from '@vladmandic/face-api';
const BLOOD_GROUPS = [
  "A+",
  "A-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "O+",
  "O-",
  "Unknown",
];
const RELATIONS = [
  "Father",
  "Mother",
  "Brother",
  "Sister",
  "Husband",
  "Wife",
  "Son",
  "Daughter",
  "Uncle",
  "Aunt",
  "Cousin",
  "Friend",
  "Other",
];

const INITIAL_FORM = {
  fullName: "",
  age: "",
  email:"",
  gender: "",
  cnic: "",
  bloodGroup: "Unknown",
  photo: null,
  whatsapp: "",
  address: "",
  city: "",
  district: "",
  emergencyName: "",
  emergencyRelation: "",
  emergencyPhone: "",
};

// ── Face image validator ───────────────────────────────────────
async function validateFaceImage(file, modelsReady) {
  const allowed = ["image/jpeg", "image/png", "image/webp"];

  if (!allowed.includes(file.type)) {
    return "Only JPG, PNG or WebP images are allowed.";
  }
  if (file.size > 5 * 1024 * 1024) {
    return "Image must be smaller than 5 MB.";
  }
  if (!modelsReady) {
    return "Face detection is still loading. Please try again in a moment.";
  }

  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => resolve({ image, url });
    image.onerror = () => reject(new Error("Could not read image"));
    image.src = url;
  }).catch(() => null);

  if (!img) return "Could not read the image. Please try a different file.";

  if (img.image.width < 100 || img.image.height < 100) {
    URL.revokeObjectURL(img.url);
    return "Image is too small. Please upload a clear face photo (min 100×100 px).";
  }

  const detections = await faceapi.detectAllFaces(
    img.image,
    new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 })
  );

  URL.revokeObjectURL(img.url);

  if (detections.length === 0) {
    return "No face detected. Please upload a clear photo of your face.";
  }
  if (detections.length > 1) {
    return "Multiple faces detected. Please upload a photo of yourself only.";
  }

  return null; // valid
}

/* ── Reusable Components ── */
class SectionTitle extends Component {
  render() {
    const { icon, title } = this.props;
    return (
      <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom border-danger-subtle">
        <span style={{ fontSize: "20px" }}>{icon}</span>
        <h5 className="mb-0 fw-bold text-danger">{title}</h5>
      </div>
    );
  }
}

class Field extends Component {
  render() {
    const { label, error, children, className = "" } = this.props;
    return (
      <div className={`mb-3 ${className}`}>
        {label && (
          <label className="form-label fw-semibold small">{label}</label>
        )}
        {children}
        {error && <div className="text-danger small mt-1">{error}</div>}
      </div>
    );
  }
}

/* ── Main Component ── */
class RegisterDonor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      form: { ...INITIAL_FORM },
      photoPreview: null,
      submitted: false,
      errors: {},
      cities: [],
    };

    this.handleChange = this.handleChange.bind(this);
    this.handlePhoto = this.handlePhoto.bind(this);
    this.handleCNIC = this.handleCNIC.bind(this);
    this.handleWhatsApp = this.handleWhatsApp.bind(this);
    this.handleEmergencyPhone = this.handleEmergencyPhone.bind(this);
    this.handleAge = this.handleAge.bind(this);
    this.handleCityChange = this.handleCityChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.resetForm = this.resetForm.bind(this);
  }

componentDidMount() {
  fetch(`${process.env.REACT_APP_API_BASE_URL}/api/cities`)
    .then((res) => res.json())
    .then((data) => {
      if (data.status) this.setState({ cities: data.data });
    })
    .catch((err) => console.error("Failed to load cities:", err));

  const MODEL_URL = "https://justadudewhohacks.github.io/face-api.js/models";

  faceapi.nets.tinyFaceDetector
    .loadFromUri(MODEL_URL)
    .then(() => this.setState({ modelsReady: true }))
    .catch((err) => console.error("Face model load failed:", err));
}
  handleChange(e) {
    const { name, value } = e.target;
    this.setState((prev) => ({
      form: { ...prev.form, [name]: value },
      errors: { ...prev.errors, [name]: "" },
    }));
  }
  handleCityChange(e) {
    const cityId = e.target.value;
    const selectedCity = this.state.cities.find(
      (c) => String(c.id) === String(cityId)
    );

    this.setState((prev) => ({
      form: {
        ...prev.form,
        city: cityId,
        district: selectedCity ? selectedCity.district_name || "" : "",
      },
      errors: { ...prev.errors, city: "" },
    }));
  }

async handlePhoto(e) {
    const file = e.target.files[0];
    if (!file) return;

    const error = await validateFaceImage(file);

    if (error) {
      e.target.value = "";
      this.setState((prev) => ({
        form: { ...prev.form, photo: null },
        photoPreview: null,
        errors: { ...prev.errors, photo: error },
      }));
      return;
    }

    this.setState((prev) => ({
      form: { ...prev.form, photo: file },
      photoPreview: URL.createObjectURL(file),
      errors: { ...prev.errors, photo: "" },
    }));
  }

  handleAge(e) {
    // digits only, max 3 characters
    const digits = e.target.value.replace(/\D/g, "").slice(0, 3);
    this.setState((prev) => ({
      form: { ...prev.form, age: digits },
      errors: { ...prev.errors, age: "" },
    }));
  }

  formatCNIC(val) {
    const digits = val.replace(/\D/g, "").slice(0, 13);
    if (digits.length <= 5) return digits;
    if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
  }

  handleCNIC(e) {
    const formatted = this.formatCNIC(e.target.value);
    this.setState((prev) => ({
      form: { ...prev.form, cnic: formatted },
      errors: { ...prev.errors, cnic: "" },
    }));
  }

  formatWhatsApp(val) {
    const digits = val.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }

  handleWhatsApp(e) {
    const formatted = this.formatWhatsApp(e.target.value);
    this.setState((prev) => ({
      form: { ...prev.form, whatsapp: formatted },
      errors: { ...prev.errors, whatsapp: "" },
    }));
  }

  formatEmergencyPhone(val) {
    const digits = val.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  }

  handleEmergencyPhone(e) {
    const formatted = this.formatEmergencyPhone(e.target.value);
    this.setState((prev) => ({
      form: { ...prev.form, emergencyPhone: formatted },
      errors: { ...prev.errors, emergencyPhone: "" },
    }));
  }

  validate() {
    const { form } = this.state;
    const errs = {};

    // ── Mandatory fields ─────────────────────────────
    if (!form.fullName.trim()) errs.fullName = "Full name is required.";
    else if (/\d/.test(form.fullName))
      errs.fullName = "Name must not contain numbers.";

    if (!form.age.trim()) {
      errs.age = "Age is required.";
    } else {
      const ageNum = parseInt(form.age, 10);
      if (isNaN(ageNum) || ageNum < 15 || ageNum > 65) {
        errs.age = "Age must be between 15 and 65.";
      }
    }

    if (!form.gender) errs.gender = "Select gender.";
    if (!form.bloodGroup) errs.bloodGroup = "Select blood group.";
    if (!form.city) errs.city = "Select a city.";

    // ── Optional fields — only validated if the user filled them in ──
    if (form.cnic.trim()) {
      const cnicDigits = form.cnic.replace(/\D/g, "");
      if (cnicDigits.length !== 13)
        errs.cnic = "CNIC must be 13 digits (e.g. 37106-8234782-3).";
    }

    if (form.whatsapp.trim()) {
      const waDigits = form.whatsapp.replace(/\D/g, "");
      if (waDigits.length !== 11)
        errs.whatsapp = "Enter a valid number (e.g. 0313-5495655).";
    }

    if (form.emergencyName.trim() && /\d/.test(form.emergencyName))
      errs.emergencyName = "Name must not contain numbers.";

    if (form.emergencyPhone.trim()) {
      const emergDigits = form.emergencyPhone.replace(/\D/g, "");
      if (emergDigits.length !== 11)
        errs.emergencyPhone = "Enter a valid number (e.g. 0300-1234567).";
    }

    return errs;
  }

 async handleSubmit(e) {
  e.preventDefault();
  const errs = this.validate();
  if (Object.keys(errs).length > 0) {
    this.setState({ errors: errs });
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  const { form } = this.state;
  const formData = new FormData();

  Object.entries(form).forEach(([k, v]) => {
    if (k === "photo" && v) {
      formData.append(k, v);
    } else if (k !== "photo" && v !== undefined && v !== null) {
      formData.append(k, v);
    }
  });

  try {
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/donors/register`, {
      method: "POST",
      body: formData,
    });

    const responseData = await res.json();
    
    if (res.ok) {
      this.setState({ submitted: true });
      if (this.props.onSuccess) {
        this.props.onSuccess();
      }
    } else {
      // Handle validation errors
      if (responseData.errors) {
        // Set errors in state to show on form
        this.setState({ 
          errors: responseData.errors 
        });
        
        // Scroll to first error
        const firstErrorField = Object.keys(responseData.errors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
        if (errorElement) {
          errorElement.focus();
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        alert("Please fix the validation errors in the form.");
      } else {
        alert("Submission failed: " + (responseData.message || "Please try again."));
      }
      console.error("Registration errors:", responseData.errors);
    }
  } catch (error) {
    console.error("Registration error:", error);
    alert("Unable to connect to the server. Please try again.");
  }
}

  resetForm() {
    this.setState({
      form: { ...INITIAL_FORM },
      photoPreview: null,
      submitted: false,
      errors: {},
    });
  }

  render() {
    const { form, errors, photoPreview, submitted, cities } = this.state;

if (submitted) {
  return (
    <div className="min-vh-100 pt-5 mt-3 bg-light d-flex align-items-center justify-content-center py-5">
      <div className="card shadow-sm border-0 text-center p-5" style={{ maxWidth: 480, width: "100%" }}>
        <div style={{ fontSize: 56 }}>✅</div>
        <h3 className="fw-bold text-success mt-3">Registration Successful!</h3>
        <p className="text-muted mt-2">
          Thank you, <strong>{form.fullName}</strong>, for registering as a blood donor with AWT Blood Bank. Our team will review your information shortly.
        </p>
        <div className="d-flex gap-2 justify-content-center mt-3">
          <button className="btn btn-outline-secondary" onClick={this.resetForm}>
            Register Another Donor
          </button>
          {this.props.onClose && (
            <button className="btn btn-danger" onClick={this.props.onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

    return (
      <div className="min-vh-100 pt-5 mt-4 bg-light">
        <div
          className="text-white py-4"
          style={{
            background: "linear-gradient(135deg, #7f1d1d 0%, #dc3545 100%)",
          }}
        >
          <div className="container" style={{ maxWidth: 680 }}>
            <h4 className="fw-bold mb-1">Donor Registration</h4>
            <p className="mb-0 opacity-75 small">AWT Blood Bank</p>
          </div>
        </div>

        <div className="container py-4" style={{ maxWidth: 680 }}>
          <div className="card shadow-sm border-0">
            <div className="card-body p-4">
              <form onSubmit={this.handleSubmit}>
                {/* Personal Information */}
                <SectionTitle icon="👤" title="Personal Information" />

                <div className="row g-3">
                  <div className="col-md-6">
                    <Field label="Full Name *" error={errors.fullName}>
                      <input
                        className={`form-control ${errors.fullName ? "is-invalid" : ""}`}
                        name="fullName"
                        value={form.fullName}
                        onChange={this.handleChange}
                        placeholder="As per CNIC"
                      />
                    </Field>
                  </div>
                  <div className="col-6">
                    <Field label="CNIC Number" error={errors.cnic}>
                      <input
                        className={`form-control ${errors.cnic ? "is-invalid" : ""}`}
                        name="cnic"
                        value={form.cnic}
                        onChange={this.handleCNIC}
                        placeholder="XXXXX-XXXXXXX-X"
                        maxLength={15}
                      />
                    </Field>
                  </div>

                  <div className="col-md-6">
                    <Field label="Age *" error={errors.age}>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={`form-control ${errors.age ? "is-invalid" : ""}`}
                        name="age"
                        value={form.age}
                        onChange={this.handleAge}
                        placeholder="e.g. 25"
                        maxLength={3}
                      />
                      <div className="text-muted small mt-1">
                        Donor must be between 15 and 65 years old.
                      </div>
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Gender *" error={errors.gender}>
                      <select
                        className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                        name="gender"
                        value={form.gender}
                        onChange={this.handleChange}
                      >
                        <option value="">-- Select --</option>
                        <option>Male</option>
                        <option>Female</option>
                        <option>Other</option>
                      </select>
                    </Field>
                  </div>

                  <div className="col-md-6">
                    <Field label="Blood Group *" error={errors.bloodGroup}>
                      <select
                        className={`form-select ${errors.bloodGroup ? "is-invalid" : ""}`}
                        name="bloodGroup"
                        value={form.bloodGroup}
                        onChange={this.handleChange}
                      >
                        <option value="">-- Select Blood Group --</option>
                        {BLOOD_GROUPS.map((g) => (
                          <option key={g}>{g}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                </div>

                {/* Contact Information */}
                <div className="mt-4">
                  <SectionTitle icon="📞" title="Contact Information" />
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Field label="WhatsApp Number" error={errors.whatsapp}>
                        <input
                          className={`form-control ${errors.whatsapp ? "is-invalid" : ""}`}
                          name="whatsapp"
                          value={form.whatsapp}
                          onChange={this.handleWhatsApp}
                          placeholder="XXXX-XXXXXXX"
                          maxLength={12}
                        />
                      </Field>
                    </div>
                 
                    <div className="col-md-6">
                      <Field label="City *" error={errors.city}>
                        <select
                          className={`form-select ${errors.city ? "is-invalid" : ""}`}
                          name="city"
                          value={form.city}
                          onChange={this.handleCityChange}
                        >
                          <option value="">-- Select City --</option>
                          {cities.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    {form.city && (
                      <div className="col-md-6">
                        <Field label="District">
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={form.district || "Not available"}
                            readOnly
                          />
                        </Field>
                      </div>
                    )}

                    <div className="col-12">
                      <Field label="Complete Address" error={errors.address}>
                        <textarea
                          className={`form-control ${errors.address ? "is-invalid" : ""}`}
                          name="address"
                          value={form.address}
                          onChange={this.handleChange}
                          rows={3}
                          placeholder="House no., Street, Area, District"
                        />
                      </Field>
                    </div>
                    
                  </div>
                </div>

                {/* Donation Preferences */}
                <div className="mt-4">
                  <SectionTitle icon="🩸" title="Emergency Detail" />

                  <div className="row g-3">
                    <div className="col-12">
                      <Field label="Full Name" error={errors.emergencyName}>
                        <input
                          className={`form-control ${errors.emergencyName ? "is-invalid" : ""}`}
                          name="emergencyName"
                          value={form.emergencyName}
                          onChange={this.handleChange}
                          placeholder="Emergency contact's name"
                        />
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field
                        label="Relationship"
                        error={errors.emergencyRelation}
                      >
                        <select
                          className={`form-select ${errors.emergencyRelation ? "is-invalid" : ""}`}
                          name="emergencyRelation"
                          value={form.emergencyRelation}
                          onChange={this.handleChange}
                        >
                          <option value="">-- Select Relationship --</option>
                          {RELATIONS.map((r) => (
                            <option key={r}>{r}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <div className="col-md-6">
                      <Field
                        label="Phone Number"
                        error={errors.emergencyPhone}
                      >
                        <input
                          className={`form-control ${errors.emergencyPhone ? "is-invalid" : ""}`}
                          name="emergencyPhone"
                          value={form.emergencyPhone}
                          onChange={this.handleEmergencyPhone}
                          placeholder="XXXX-XXXXXXX"
                          maxLength={12}
                        />
                      </Field>
                    </div>
                      <div className="d-flex align-items-start gap-3 mb-4">
                  <label style={{ cursor: "pointer", flexShrink: 0 }}>
                    <div
                      className="rounded-circle border border-2 border-danger d-flex align-items-center justify-content-center overflow-hidden bg-danger-subtle"
                      style={{ width: 90, height: 90 }}
                    >
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="Preview"
                          className="w-100 h-100 object-fit-cover"
                        />
                      ) : (
                        <div className="text-center text-danger small px-1">
                          <div style={{ fontSize: 10, lineHeight: 1.2 }}>
                            Face
                            <br />
                            Photo
                          </div>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      capture="user"
                      onChange={this.handlePhoto}
                      className="d-none"
                    />
                  </label>
                  <div className="pt-1">
                    <p className="fw-semibold small mb-1">
                      Face Photo <span className="text-muted">(optional only used for life saving card)</span>
                    </p>
                    <p className="text-muted small mb-1" style={{ fontSize: 12 }}>
                      • Upload a clear photo of <strong>your face only</strong>
                      <br />
                      • The photo will be used to verify your identity when issuing a life saving card <br />
                      • JPG, PNG or WebP — max 5 MB
                      <br />• Minimum size: 100 × 100 px
                    </p>
                    {errors.photo && (
                      <div className="text-danger small">{errors.photo}</div>
                    )}
                  </div>
                </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                  <button
                    type="submit"
                    className="btn px-4 text-white fw-semibold"
                    style={{ background: "#7f1d1d" }}
                  >
                    Submit Registration
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default RegisterDonor;